import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject, Optional } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';
import { ProductsService } from '@app/services/products/product.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { selectOrganizationState } from '@app/modules/organization/store/selectors/organization.selectors';
import * as OrganizationActions from '@app/modules/organization/store/actions/organization.actions';
import { ProductStorageTreeviewComponent } from '@app/shared/components/product-storage-treeview/product-storage-treeview.component';
import { BreakpointService } from '@app/services/breakpoint/breakpoint.service';

interface CatalogProduct {
  prdID: number;
  prdCode: string;
  prdNameEn: string;
  prdCatID?: number;
  prdCategoryName?: string;
  prdIsAsset?: number;
}

@Component({
  selector: 'app-add-existing-product-dialog',
  templateUrl: './add-existing-product-dialog.component.html',
  styleUrls: ['./add-existing-product-dialog.component.scss']
})
export class AddExistingProductDialogComponent implements OnInit {
  searchControl = new FormControl('');
  isLoading = false;
  isSubmitting = false;
  products: CatalogProduct[] = [];
  selection = new SelectionModel<number>(true, []);
  displayedColumns = ['select', 'prdCode', 'prdNameEn', 'prdCategory'];
  // Optional storage target. Empty = products join the location unassigned,
  // which is the previous behaviour.
  selectedStorages: { storageStructureID: number; item: string; path: string }[] = [];
  private categoryNameMap: { [id: number]: string } = {};

  constructor(
    private dialogRef: MatDialogRef<AddExistingProductDialogComponent>,
    private _products: ProductsService,
    private _toastr: ToastrService,
    private _translation: TranslateService,
    private store$: Store,
    private _matDialog: MatDialog,
    private _breakpoints: BreakpointService,
    // Opened from the add-product form with the SKU that was rejected, so the
    // user lands on the product they were trying to create.
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {}

  ngOnInit(): void {
    // Load categories so we can translate prdCatID -> name in the table.
    this.store$.dispatch(OrganizationActions.loadCategories());
    this.store$.select(selectOrganizationState).subscribe(org => {
      const cats = org?.categories || [];
      this.categoryNameMap = {};
      for (const c of cats) {
        if (c?.prdCatID != null) {
          this.categoryNameMap[c.prdCatID] = c.prdCatNameEn || '';
        }
      }
      // Re-attach names to already-loaded products (search may finish before categories arrive).
      this.products = this.products.map(p => ({
        ...p,
        prdCategoryName: p.prdCatID ? this.categoryNameMap[p.prdCatID] : ''
      }));
    });

    const prefill = (this.dialogData?.prefillSearch || '').toString().trim();
    if (prefill) {
      this.searchControl.setValue(prefill, { emitEvent: false });
    }
    this.search(prefill);

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => this.search(value || ''));
  }

  private search(query: string): void {
    this.isLoading = true;
    // prdStorage=0 → fetch the franchise catalog (no location filter on storage),
    // so users can pick from any product their franchise already has.
    this._products.getProducts('prdNameEn', 'asc', query, '', 0, undefined, undefined, true)
      .pipe(first())
      .subscribe({
        next: (res: any) => {
          // Only show products NOT already in this location, and exclude assets.
          //  - loc_movement_count > 0 → product already has active PMH rows
          //    here (a NULL-storage placeholder also counts as membership)
          //  - prdIsAsset === 1   → assets are single-home by design and are
          //    transferred via the dedicated asset transfer flow, not via this
          //    catalog "add" path. Backend's excludeAssets filter is opt-in,
          //    so we filter client-side to keep other callers untouched.
          this.products = (res?.products || [])
            .filter((p: any) => !(p.loc_movement_count > 0))
            .filter((p: any) => !p.prdIsAsset)
            .map((p: any) => ({
              prdID: p.prdID,
              prdCode: p.prdCode,
              prdNameEn: p.prdNameEn,
              prdCatID: p.prdCatID,
              prdCategoryName: p.prdCatID ? this.categoryNameMap[p.prdCatID] : '',
              prdIsAsset: p.prdIsAsset
            }));
          this.isLoading = false;
        },
        error: () => {
          this.products = [];
          this.isLoading = false;
        }
      });
  }

  toggleAll(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.products.forEach(p => this.selection.select(p.prdID));
    }
  }

  isAllSelected(): boolean {
    return this.products.length > 0 && this.products.every(p => this.selection.isSelected(p.prdID));
  }

  isIndeterminate(): boolean {
    return this.selection.hasValue() && !this.isAllSelected();
  }

  openStoragePicker(): void {
    const dialog = this._matDialog.open(ProductStorageTreeviewComponent, {
      width: this._breakpoints.isMobile() === true ? '100vw' : '30vw',
      data: {
        storages: this.selectedStorages,
        isFilter: false,
        transfer: true,
        title: this._translation.instant('products.bulkStorageAdd')
      }
    });

    dialog.afterClosed().pipe(first()).subscribe(res => {
      if (res === undefined || res === null || res === 'cancel') { return; }
      this.selectedStorages = (res || []).map((storage: any) => ({
        storageStructureID: storage.storageStructureID,
        item: storage.item,
        path: storage.path
      }));
    });
  }

  clearStorages(): void {
    this.selectedStorages = [];
  }

  get selectedStorageLabel(): string {
    return this.selectedStorages
      .map(s => (s.path ? `${s.path} -> ${s.item}` : s.item))
      .join(', ');
  }

  onSubmit(): void {
    const ids = this.selection.selected;
    if (!ids.length) return;

    // Join the location first (the backend writes a NULL-storage placeholder),
    // then place the products into the chosen storages when one was picked.
    // Leaving the picker empty keeps the previous "unassigned" behaviour.
    this.isSubmitting = true;
    this._products.bulkAddToLocation(ids).pipe(first()).subscribe({
      next: () => {
        if (!this.selectedStorages.length) {
          this.finishSubmit(ids.length);
          return;
        }
        this._products.addProductsByStorages(ids, this.selectedStorages).pipe(first()).subscribe({
          next: () => this.finishSubmit(ids.length),
          error: () => {
            // The products did join the location; only the storage step failed,
            // so report it without pretending nothing happened.
            this.isSubmitting = false;
            this._toastr.error(this._translation.instant('seemsError'));
            this.dialogRef.close({ added: true, count: ids.length });
          }
        });
      },
      error: () => {
        this.isSubmitting = false;
        this._toastr.error(this._translation.instant('seemsError'));
      }
    });
  }

  private finishSubmit(count: number): void {
    this.isSubmitting = false;
    this._toastr.success(this._translation.instant('products.addExistingProductSuccess'));
    this.dialogRef.close({ added: true, count });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
